import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";

const CURRENCY_KEYS = new Set([
  "transaction_amount",
  "net_of_vat",
  "vat_amount",
  "claim_amount",
  "withholding_tax",
  "ppd",
  "ppd_withholding_tax",
  "net_payable",
  "funded_amt",
]);

const formatCurrency = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return value || "";

  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const toDisplayValue = (row, key) => {
  const raw = row?.[key];
  if (raw === null || raw === undefined) return "";
  if (CURRENCY_KEYS.has(key)) return formatCurrency(raw);
  return String(raw);
};

const DEFAULT_DPR_TABLE_STATE_STORAGE_KEY = "providerPayment.dpr.tableState.v1";
const DPR_LIST_ROUTE = "/transaction-reimbursement-dpr";

const persistDprTableState = (tableState, storageKey = DEFAULT_DPR_TABLE_STATE_STORAGE_KEY) => {
  if (!tableState || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(tableState));
  } catch (error) {
    // Ignore storage failures so the full-page view can still navigate back.
  }
};

const DetailValue = ({ value }) => {
  if (!value) {
    return <div className="text-sm text-slate-400">N/A</div>;
  }

  if (value.length > 90 || value.includes("\n")) {
    return (
      <textarea
        readOnly
        value={value}
        className="min-h-[7rem] w-full resize-y rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-800 outline-none"
      />
    );
  }

  return <div className="break-words text-sm font-medium text-slate-900">{value}</div>;
};

const DetailedPaymentReportFullViewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const row = location.state?.row || null;
  const columns = Array.isArray(location.state?.columns) ? location.state.columns : [];
  const returnTo = location.state?.returnTo || DPR_LIST_ROUTE;
  const tableState = location.state?.tableState || null;
  const tableStateStorageKey = location.state?.tableStateStorageKey || DEFAULT_DPR_TABLE_STATE_STORAGE_KEY;
  const reportTitle = location.state?.reportTitle || "Detailed Payment Report";

  const handleBackToDpr = () => {
    persistDprTableState(tableState, tableStateStorageKey);

    if (typeof window !== "undefined" && window.history.length > 1 && location.key !== "default") {
      navigate(-1);
      return;
    }

    navigate(returnTo);
  };

  if (!row) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <button
            type="button"
            onClick={handleBackToDpr}
            className="mb-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
            Back
          </button>
          <h1 className="text-xl font-bold text-slate-900">{reportTitle}</h1>
          <p className="mt-2 text-sm text-slate-500">
            No row data is available for this full-page view. Open it from the report table or row modal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[92rem] space-y-5">
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{reportTitle} Full View</h1>
              <p className="mt-1 text-sm text-slate-500">
                Transaction {toDisplayValue(row, "trans_no") || "N/A"} / Payment Ref. {toDisplayValue(row, "payment_ref_no") || "N/A"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleBackToDpr}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faAngleLeft} />
            Back to Report
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          {[
            ["PPD Term", "ppd_term"],
            ["Payment Mode", "payment_mode"],
            ["TAT", "tat"],
            ["Date Paid", "date_paid"],
          ].map(([label, key]) => (
            <div key={key} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
              <div className="mt-2 break-words text-lg font-bold text-slate-900">
                {toDisplayValue(row, key) || "N/A"}
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {columns.map((column) => {
            const value = toDisplayValue(row, column.key);
            return (
              <div key={column.key} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  {column.label}
                </div>
                <DetailValue value={value} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DetailedPaymentReportFullViewPage;
